import { useEffect } from 'react'

import {
	Box,
	BoxProps,
	CircularProgress,
	CircularProgressLabel,
	UseControllableStateProps,
	useControllableState
} from '@chakra-ui/react'

import DoneRoundIcon from '@wwt/shared/assets/icons/outline/done-round.svg'

interface CountdownTimerProps
	extends Omit<BoxProps, 'defaultValue' | 'onChange'>,
		UseControllableStateProps<number> {
	max: number
	isStopped: boolean
	onClose?: () => void
}

const secondToColor: {
	[key: string]: string
} = {
	'5': 'primary.100',
	'4': 'primary.100',
	'3': 'brand.100',
	'2': 'brand.100',
	'1': 'brand.300'
}

export const CountdownTimer = ({
	max,
	isStopped,
	onClose,
	value,
	defaultValue,
	onChange,
	shouldUpdate,
	...boxProps
}: CountdownTimerProps) => {
	const [time, setTime] = useControllableState({
		value,
		defaultValue: defaultValue ?? max,
		onChange,
		shouldUpdate
	})

	useEffect(() => {
		let timerId: ReturnType<typeof setTimeout>

		const tick = () => {
			if (!isStopped) {
				if (time === 0) {
					onClose?.()
					clearInterval(timerId)
				} else {
					setTime(prevTime => prevTime - 1)
				}
			}
		}

		if (isStopped) {
			setTime(0)
		}

		if (time >= 0 && !isStopped) {
			timerId = setTimeout(tick, 1000)
		}

		return () => clearInterval(timerId)
	}, [time, isStopped])

	return (
		<Box
			pos="relative"
			w="1.5rem"
			h="1.5rem"
			borderRadius="100%"
			color="primary.100"
			{...boxProps}
		>
			{time === 0 ? (
				<DoneRoundIcon />
			) : (
				<CircularProgress
					value={time}
					max={max}
					size="1.5rem"
					thickness="0.3125rem"
					color={secondToColor[time.toString()]}
					transform="scaleX(-1)"
					trackColor="whiteAlpha.100"
				>
					<CircularProgressLabel
						color={secondToColor[time.toString()]}
						fontSize="md"
						transform="translate(-50%, -50%) scaleX(-1)"
					>
						{time}
					</CircularProgressLabel>
				</CircularProgress>
			)}
		</Box>
	)
}
